import React, { useMemo, useRef, useState } from "react";

import Radio from "components/radio";
import { Dropdown } from "components/dropdown/dropdown";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";

import {
  getEffectiveReservationMaxBookingSize,
  getExtraGuestCounts,
  getPrimaryGuestButtonCount,
  RESERVATION_VISIBLE_GUEST_BUTTONS,
} from "./reservationBookingSize";
import "./reservationGuestCountSelector.scss";

interface IProps {
  name: string;
  value: number;
  maxBookingSize?: number | null;
  onChange: (amount: number) => void;
  disabled?: boolean;
}

export default function ReservationGuestCountSelector(props: IProps) {
  const { name, value, maxBookingSize, onChange, disabled = false } = props;

  const [extraGuestsDropdownVisible, setExtraGuestsDropdownVisible] = useState(false);
  const extraGuestsRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(extraGuestsRef, () => setExtraGuestsDropdownVisible(false));

  const effectiveMaxBookingSize = useMemo(
    () => getEffectiveReservationMaxBookingSize(maxBookingSize),
    [maxBookingSize],
  );
  const primaryGuestButtonCount = useMemo(
    () => getPrimaryGuestButtonCount(effectiveMaxBookingSize),
    [effectiveMaxBookingSize],
  );
  const extraGuestCounts = useMemo(
    () => getExtraGuestCounts(effectiveMaxBookingSize),
    [effectiveMaxBookingSize],
  );
  const primaryGuestCounts = useMemo(
    () => Array.from({ length: primaryGuestButtonCount }, (_, index) => index + 1),
    [primaryGuestButtonCount],
  );

  function handleExtraGuestsVisible() {
    if (disabled) {
      return;
    }

    setExtraGuestsDropdownVisible(true);
  }

  function selectExtraGuestCount(quantity: number) {
    onChange(quantity);
    setExtraGuestsDropdownVisible(false);
  }

  function extraGuestsMenu() {
    return (
      <ul className="extra-players-menu">
        {extraGuestCounts.map(guestCount => (
          <li
            key={guestCount}
            className="extra-players-menu-item"
            onClick={() => selectExtraGuestCount(guestCount)}
          >
            <span>{guestCount}</span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="reservation-guest-count-selector">
      <Radio.Group
        name={name}
        onChange={(nextValue: string | number | Record<string, any>) => onChange(Number(nextValue))}
        value={value}
      >
        {primaryGuestCounts.map(guestCount => (
          <Radio.Button key={guestCount} value={guestCount} disabled={disabled}>
            {guestCount}
          </Radio.Button>
        ))}
        {extraGuestCounts.length > 0 ? (
          <Radio.Button
            onClick={handleExtraGuestsVisible}
            value={value > RESERVATION_VISIBLE_GUEST_BUTTONS ? value : -1}
            disabled={disabled}
          >
            {value > RESERVATION_VISIBLE_GUEST_BUTTONS ? value : "+"}
          </Radio.Button>
        ) : null}
      </Radio.Group>
      {extraGuestCounts.length > 0 ? (
        <Dropdown
          overlay={extraGuestsMenu()}
          trigger="boolean"
          placement="topLeft"
          className="self-center extra-players-menu"
          showMenu={extraGuestsDropdownVisible}
          propRef={extraGuestsRef}
          outSideClickDisable={true}
        >
          <span className="reservation-guest-count-selector__dropdown-anchor" />
        </Dropdown>
      ) : null}
    </div>
  );
}
